import { http, user, helper, webauthn } from 'services'
import { jwtDecode } from 'jwt-decode';
import { sha256 } from 'js-sha256';
// initial state
const state = () => ({
  token: user.getToken(),
  data: user.getUser(),
  errors: null,
  twoFactorRequired: false,
  twoFactorChallenge: null
})

// getters
const getters = {
}

function payload (resOrErr) {
  if (!resOrErr) return {};
  if (resOrErr.data && !resOrErr.response) return resOrErr.data;
  if (resOrErr.response && resOrErr.response.data) return resOrErr.response.data;
  return {};
}

function extractChallenge (data) {
  const nested = data && data.data && typeof data.data === 'object' ? data.data : {};
  const value = (data && (data.challenge || data.two_factor_challenge || data['2fa_challenge'])) ||
    nested.challenge || nested.two_factor_challenge || nested['2fa_challenge'];
  if (value === undefined || value === null || value === true || value === false) return null;
  return String(value);
}

function isTwoFactorChallenge (resOrErr) {
  const data = payload(resOrErr);
  const nested = data.data && typeof data.data === 'object' ? data.data : {};
  if (extractChallenge(data)) return true;
  return !!(
    data.two_factor_required ||
    data.requires_2fa ||
    data.requires_two_factor ||
    data['2fa_required'] ||
    data.status === '2fa_required' ||
    data.step === '2fa' ||
    nested.two_factor_required ||
    nested.requires_2fa ||
    nested.requires_two_factor
  );
}

function completeLogin (commit, res, fingerprint) {
  const decodedToken = jwtDecode(res.data.token);
  if (!decodedToken.ffp) {
    throw new Error('Invalid token: fingerprint missing');
  }
  const token = res.data.token;
  if (token) {
    fingerprint = sha256(fingerprint + '');
    let tokenFingerprint = jwtDecode(token).ffp;
    const isValid = fingerprint === tokenFingerprint;
    if (!isValid) {
      throw new Error('Invalid token: fingerprint mismatch');
    }
  }
  commit('USER_LOGIN', res);
  helper.startUserValidationTimer();
}

function twoFactorFlags (enabled) {
  return {
    two_factor_enabled: enabled,
    google2fa_enabled: enabled
  };
}

// actions
const actions = {
  ['user.login'] ({ commit }, {email, password, token, fingerprint}) {
    commit('CLEAR_ERRORS');
    commit('CLEAR_2FA');
    return (http.post(`users/auth`, 
      {email, password, "g-recaptcha-response": token},
      { headers: { 'X-Front-Fingerprint': fingerprint } }
    ))
    .then((res) => {
      if (isTwoFactorChallenge(res)) {
        commit('USER_2FA_REQUIRED', payload(res));
        return;
      }
      try {
        completeLogin(commit, res, fingerprint);
      } catch (error) {
        commit('USER_ERROR', {err: error});
      }
    })
    .catch((err) => {
      if (isTwoFactorChallenge(err)) {
        commit('USER_2FA_REQUIRED', payload(err));
        return;
      }
      commit('USER_ERROR', {err});
    });
  },
  ['user.verify2fa'] ({ commit, state }, {code, fingerprint, email, challenge}) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/auth/2fa`,
      {
        code,
        email,
        challenge: challenge || state.twoFactorChallenge
      },
      { headers: { 'X-Front-Fingerprint': fingerprint } }
    ))
    .then((res) => {
      try {
        completeLogin(commit, res, fingerprint);
      } catch (error) {
        commit('USER_ERROR', {err: error, clearUser: false});
      }
    })
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
    });
  },
  ['user.loginPasskey'] ({ commit }, {email, fingerprint, mediation = 'optional', signal}) {
    commit('CLEAR_ERRORS');
    commit('CLEAR_2FA');
    const body = email ? { email } : {};
    const config = {
      headers: { 'X-Front-Fingerprint': fingerprint },
      skipAuth: true
    };

    return http.post(`users/auth/passkey/options`, body, config)
    .then((res) => webauthn.get(res.data, mediation, signal))
    .then((credential) => http.post(
      `users/auth/passkey`,
      { credential },
      config
    ))
    .then((res) => {
      completeLogin(commit, res, fingerprint);
      return res;
    })
    .catch((err) => {
      if (!err || err.name !== 'AbortError') {
        commit('USER_ERROR', {err, clearUser: false});
      }
      return Promise.reject(err);
    });
  },
  ['user.listPasskeys'] ({ commit }) {
    commit('CLEAR_ERRORS');
    return http.get(`users/passkeys`)
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
      return Promise.reject(err);
    });
  },
  ['user.registerPasskey'] ({ commit }, name) {
    commit('CLEAR_ERRORS');
    return http.get(`users/passkeys/options`)
    .then((res) => webauthn.create(res.data))
    .then((credential) => {
      const data = { credential };
      if (name) data.name = name;
      return http.post(`users/passkeys`, data);
    })
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
      return Promise.reject(err);
    });
  },
  ['user.deletePasskey'] ({ commit }, id) {
    commit('CLEAR_ERRORS');
    return http.delete(`users/passkeys/${encodeURIComponent(id)}`)
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
      return Promise.reject(err);
    });
  },
  ['user.setup2fa'] ({ commit }) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/2fa/setup`))
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
    });
  },
  ['user.enable2fa'] ({ commit }, data = {}) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/2fa/enable`, data))
    .then((res) => {
      const body = payload(res);
      const nested = body.data && typeof body.data === 'object' ? body.data : {};
      const userPayload = nested.id ? nested : twoFactorFlags(true);
      commit('USER_UPDATED', { data: _.assign({}, userPayload, twoFactorFlags(true)) });
      return res;
    })
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
    });
  },
  ['user.disable2fa'] ({ commit }, data = {}) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/2fa/disable`, data))
    .then((res) => {
      const body = payload(res);
      const nested = body.data && typeof body.data === 'object' ? body.data : {};
      const userPayload = nested.id ? nested : twoFactorFlags(false);
      commit('USER_UPDATED', { data: _.assign({}, userPayload, twoFactorFlags(false)) });
      return res;
    })
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
    });
  },
  ['user.clearTwoFactorChallenge'] ({ commit }) {
    commit('CLEAR_2FA');
  },
  ['user.logout'] ({ commit }) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/auth/logout`))
    .then((res) => {
      commit('USER_LOGOUT');
      helper.stopUserValidationTimer();
    })
    .catch((err) => {
      commit('USER_ERROR', {err});
    });
  },
  ['user.signup'] ({ commit }, data) {
    commit('CLEAR_ERRORS');
  },
  ['user.updatePersonalData'] ({ commit }, data) {
    commit('CLEAR_ERRORS');

    const profileData = _.cloneDeep(data);
    let roles = profileData.roles;
    delete profileData.roles;
    delete profileData.two_fa_action;
    console.log(profileData);
    return (http.put(`users/update-profile`, profileData))
    .then((res) => {
      res.data.roles = roles;
      commit('USER_UPDATED', res);
    })
    .catch((err) => {
      commit('USER_ERROR', {err, clearUser: false});
    });
  },
  ['user.forgotPassword'] ({ commit }, email) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/reset-password`, {email}))
    .catch((err) => {
      commit('USER_ERROR', {err});
    });
  },
  ['user.resetPassword'] ({ commit }, {token, password, welcome}) {
    commit('CLEAR_ERRORS');
    return (http.post(`users/reset-user-password`, {token, password, welcome}))
    .catch((err) => {
      commit('USER_ERROR', {err});
    });
  },
}

// mutations
const mutations = {
  USER_LOGIN (state, res) {
    state.token = res.data.token;
    state.data = res.data.data;
    state.data.locations = res.data.locations;
    state.data.channels = res.data.channels;
    state.twoFactorRequired = false;
    state.twoFactorChallenge = null;
    state.errors = null;

    user.setUser(state.data);
    user.setToken(state.token);
  },
  USER_UPDATED (state, res) {
    state.data = _.assign(state.data, res.data);
    user.setUser(state.data);
  },
  USER_2FA_REQUIRED (state, data) {
    state.twoFactorRequired = true;
    state.twoFactorChallenge = extractChallenge(data || {});
    state.errors = null;
    const token = data && (data.temp_token || data.two_factor_token);
    if (token) {
      state.token = token;
      user.setToken(token);
    }
  },
  CLEAR_2FA (state) {
    state.twoFactorRequired = false;
    state.twoFactorChallenge = null;
    if (!state.data) {
      state.token = null;
      user.setToken(null);
    }
  },
  USER_LOGOUT (state) {
    state.token = null;
    state.data = null;
    state.twoFactorRequired = false;
    state.twoFactorChallenge = null;

    user.setUser(state.data);
    user.setToken(state.token);
    sessionStorage.setItem('__testing_mode_items__', null);
  },
  //ERRORS
  USER_ERROR (state, {err, clearUser = true}) {
    try {
      state.errors = err.response.data;
    } catch (e) {
      state.errors = err;
    }
    if (clearUser) {
      user.setUser(null);
      user.setToken(null);
    }
  },

  CLEAR_ERRORS (state) {
    state.errors = null
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
